import { IconButton, Tooltip, withStyles } from "@material-ui/core";
import { Mail } from "@material-ui/icons";

import React, { useState } from "react";
import { BooleanField, Datagrid, FunctionField, TextField } from "react-admin";
import { CustomDateField } from "../../../../../Components/CustomDateField";
import PbhNotesFiled from "../../../../../Components/ReferenceFields/Notes/PbhNotesFiled";
import { PbhRequestStatus } from "../../../../../Enums/PbhRequestStatus";
import { WaitingResponseType } from "../../../../../Enums/WaitingResponseType";
import DataGridActionsButtons from "./DataGridActionsButtons";
import RequirementCategoryToolTip from "./RequirementCategoryToolTip";
import Info from "@material-ui/icons/Info";
import { ChatBubble } from "@material-ui/icons";
import { useAppDataProvider } from "../../../../../../src/dataprovider";
import CopyableTableWrapper from "../../../../../../src/Components/CopyableTableWrapper";

export const OpenRequestsDataGrid = props => {
  const onEdit = (id, basePat, record) => {
    if (basePat == "/pbhrequestremarks/pbhrequestorders/") {
      props?.onCreateRemarks?.(record);
    } else {
      const win: any = window.open(`#/pbhrequestorders/${id}`, "_blank");
      win.focus();
    }
  };

  const isCurrentDateMoreThan15SecondsLater = (createdDate: string): boolean => {
    const givenDate: Date = new Date(createdDate);
    const threeHoursInMillis: number = 3 * 60 * 60 * 1000;
    const dateWithThreeHoursAdded: Date = new Date(givenDate.getTime() + threeHoursInMillis);
    const currentDate: Date = new Date();
    const differenceInMillis: number = currentDate.getTime() - dateWithThreeHoursAdded.getTime();
    const fifteenSecondsInMillis: number = 15 * 1000;
    return differenceInMillis < fifteenSecondsInMillis;
  };

  const RefNoField = props => (
    <FunctionField label="REF NO" render={record => <RefNoFunctionField record={record} />} />
  );

  const RefNoFunctionField = props => {
    const { record } = props;
    const dataProvider = useAppDataProvider();
    const [rejectedRefNo, setRejectedRefNo] = useState();
    const [backgroundColor, setBackgroundColor] = useState("white");
    const HtmlTooltip = props => <Tooltip {...props} />;

    React.useEffect(() => {
      if (props.record.parentRejectionOrderId != null) {
        dataProvider
          .getOne("pbhrequestorders/", {
            id: props.record.parentRejectionOrderId,
          })
          .then(response => {
            setRejectedRefNo(response.data.refNo);
          });
      }
    }, []);

    React.useEffect(() => {
      let timer;
      if (isCurrentDateMoreThan15SecondsLater(record?.createdDate)) {
        let isRed = true;
        timer = setInterval(() => {
          setBackgroundColor(isRed ? "red" : "white");
          isRed = !isRed;
        }, 500);

        return () => clearInterval(timer);
      }
    }, [record.id]);

    const getWaitingResponseIcon = () => {
      if (record?.pbhWaitingResponseStatus === WaitingResponseType.WaitingResponseFromCustomer) {
        return (
          <HtmlTooltip title="Waiting Response from Customer">
            <IconButton size="small" style={{ padding: 2 }}>
              <Mail style={{ color: "yellow" }} />
            </IconButton>
          </HtmlTooltip>
        );
      }
      if (record?.pbhWaitingResponseStatus === WaitingResponseType.Responded) {
        return (
          <HtmlTooltip title="Responded">
            <IconButton size="small" style={{ padding: 2 }}>
              <Mail style={{ color: "lightgreen" }} />
            </IconButton>
          </HtmlTooltip>
        );
      }
      return null;
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          transition: "background-color 0.5s",
          padding: "8px",
        }}
      >
        {getWaitingResponseIcon()}
        <>{record.refNo}</>
        {(record.isRejectionOrder || record.isReplenishmentRejectionOrder) && (
          <HtmlTooltip title={record.isRejectionOrder ? "Rejection Order" : "Replenishment Rejection Order"}>
            <IconButton color="primary" style={{ fontSize: "0.75rem" }}>
              {rejectedRefNo}
              <Info />
            </IconButton>
          </HtmlTooltip>
        )}
        {record.isHardTime && (
          <IconButton>
            <Tooltip title="This component is hard time">
              <ChatBubble />
            </Tooltip>
          </IconButton>
        )}
      </div>
    );
  };

  const CustomizedTooltip = withStyles(theme => ({
    tooltip: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(14),
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  return (
    <CopyableTableWrapper>
      <Datagrid optimized {...props} classes={{ table: props.classes.datagrid }} hasBulkActions={false}>
        <DataGridActionsButtons
          label={"Actions"}
          onEdit={onEdit}
          tabValue={PbhRequestStatus.Open}
          reloadTabs={props.reloadTabs}
        />
        <RefNoField label="REF NO" />
        <TextField source="customerName" label="CUSTOMER" sortable={false} />
        <TextField source="customerPo" label="CUSTOMER PO" />
        <FunctionField
          sortable={false}
          label="REQUIREMENT CATEGORY"
          render={(record: any) => {
            return <RequirementCategoryToolTip record={record} source={"requirementCategoryText"} />;
          }}
        />
        <FunctionField
          label="PN"
          render={record => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <span>{record?.pn}</span>
              {record?.isPnRie === true && (
                <CustomizedTooltip title="RIE">
                  <IconButton style={{ fontSize: "0.75rem" }}>
                    <Info />
                  </IconButton>
                </CustomizedTooltip>
              )}
            </div>
          )}
        />
        <FunctionField
          label="POOL SCOPE"
          render={record => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <BooleanField source="isPool" label="POOL SCOPE" />
              {record?.previousRejectedPoolScopeRemark && (
                <CustomizedTooltip title={record?.previousRejectedPoolScopeRemark}>
                  <IconButton>
                    <Info />
                  </IconButton>
                </CustomizedTooltip>
              )}
            </div>
          )}
        />
        <TextField source="airportCode" label="DESTINATION" />
        <TextField source="acRegistration" label="AC" />
        <CustomDateField source="createdDate" label="REQUEST DATE" showTime />
        <CustomDateField source="requestedDeliveryDate" label="REQUESTED DELIVERY DATE" showTime locales="en-GB" />
        <PbhNotesFiled label="PBH NOTE" />
      </Datagrid>
    </CopyableTableWrapper>
  );
};

export default OpenRequestsDataGrid;
